import { ArrowRight, FolderOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import { useOrganizationPockets } from "../hooks/useOrganizationPockets";
import { usePocketsVisibility } from "../hooks/usePocketsVisibility";

export const ActivatePocketsBanner = () => {
  const { t } = useTranslations("cards");
  const navigate = useNavigate();

  const { visible, isLoading: visibilityLoading } = usePocketsVisibility();
  const { pockets, isLoading, error } = useOrganizationPockets();

  // The feature is gated: organizations without a funded pocket never see the
  // activation nudge. (When `visible` is true the org already has pockets, so
  // the checks below short-circuit anyway.)
  if (visibilityLoading || !visible) return null;
  if (isLoading) return null;
  if (!error && pockets.length > 0) return null;

  // Laid out against its own width (`@container`): a `flex-1` text column next to the button never wrapped, so
  // on a phone the button kept its width and squeezed the copy into a word-per-line strip. Narrow, the button
  // drops under the copy; wide, they share one row.
  return (
    <div className="@container rounded-2xl border border-dashed border-border bg-muted/40 p-4">
      <div className="flex flex-col gap-3 @xl:flex-row @xl:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3 @xl:items-center">
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-card text-muted-foreground ring-1 ring-border">
            <FolderOpen aria-hidden="true" className="size-5" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{t("pockets_activate_title")}</p>
            <p className="text-xs text-muted-foreground">{t("pockets_activate_subtitle")}</p>
          </div>
        </div>

        <ColocaButton
          size="sm"
          variant="default"
          iconTrailing={ArrowRight}
          className="ml-12 self-start @xl:ml-0 @xl:self-auto"
          onClick={() => navigate("/app/cards/pockets")}
        >
          {t("pockets_activate_button")}
        </ColocaButton>
      </div>
    </div>
  );
};
